import { Link } from "react-router-dom";
import React, { useState, useMemo } from "react";
import { Search, Bell, Sun, Moon, ChevronDown, Circle, LogOut, ShieldCheck, Menu } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import { useAlerts } from "../../context/AlertsContext";
import { meta } from "../../lib/symbols";
import { SymbolCatalog } from "../../engine";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { BrandLogo } from "./BrandLogo";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUiStore } from "../../store/uiStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import AccountSelector from "./AccountSelector";

export default function TopBar({ onSelectSymbol, onMenu }) {
  const { theme } = useTheme();
  const { saveSettings } = useWorkspace();
  const mobile = useIsMobile();
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const toggle = () => saveSettings("general", { theme: theme === "dark" ? "light" : "dark" }); // persists to the user's settings, survives refresh
  const { user, logout } = useAuth();
  const { status, feed } = useMarket();
  const { unread, openManager } = useAlerts();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const entries = SymbolCatalog.all().map((s) => [s.symbol, { display: s.display, name: s.name, market: s.market_display }]);
    if (!q) return entries.filter(([code]) => /^1HZ(10|25|50|75|100)V$/.test(code)).slice(0, 6);
    return entries.filter(([code, m]) =>
      code.toLowerCase().includes(q) || m.display.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)
    ).slice(0, 10);
  }, [query]); // eslint-disable-line

  return (
    <header className="flex items-center gap-2 md:gap-4 px-2 md:px-4 h-12 md:h-16 border-b border-border bg-panel shrink-0" data-testid="topbar">
      <button data-testid="mobile-menu-btn" onClick={() => (mobile ? onMenu() : toggleSidebar())} aria-label={mobile ? "Open menu" : sidebarCollapsed ? "Show navigation" : "Hide navigation"} aria-pressed={!mobile && sidebarCollapsed}
        title={mobile ? "Menu" : sidebarCollapsed ? "Show navigation" : "Hide navigation — chart takes the full width"}
        className={`h-9 w-9 rounded-lg border flex items-center justify-center hover:text-foreground ${!mobile && sidebarCollapsed ? "border-primary/60 text-primary" : "border-border text-muted-foreground"}`}>
        <Menu className="h-5 w-5" />
      </button>
      <div className="flex items-center gap-2.5" data-testid="topbar-brand">
        <BrandLogo className="h-7 md:h-9" />
      </div>

      <div className="relative flex-1 max-w-xl hidden md:block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          data-testid="global-search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search symbols, markets, or strategies…"
          className="w-full h-10 pl-10 pr-4 rounded-lg bg-background border border-border text-sm outline-none focus:border-primary/60"
        />
        {open && results.length > 0 && (
          <div className="absolute top-12 left-0 right-0 z-50 rounded-lg border border-border bg-popover shadow-xl p-1" data-testid="search-results">
            {results.map(([code, m]) => (
              <button
                key={code}
                data-testid={`search-result-${code}`}
                onMouseDown={() => { onSelectSymbol(code); setQuery(""); setOpen(false); }}
                className="w-full flex items-center justify-between px-3 py-2 rounded hover:bg-secondary text-left"
              >
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">{m.display}</span>
                  <span className="text-[11px] text-muted-foreground">{m.name}</span>
                </span>
                <span className="text-[10px] uppercase text-muted-foreground">{m.category}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1.5 md:gap-2">
        {user?.role === "superadmin" && (
          <Link to="/admin" data-testid="admin-link" title="Platform management centre" className="hidden md:flex h-9 px-3 rounded-lg border border-primary/50 text-primary text-xs font-bold items-center gap-1.5 hover:bg-primary/10"><ShieldCheck className="h-4 w-4" /> Admin</Link>
        )}
        <button data-testid="theme-toggle" onClick={toggle} className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground">
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <button data-testid="notifications-btn" onClick={() => openManager()} title="Alerts" className="relative h-9 w-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
          )}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="profile-btn" className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-lg border border-border hover:bg-secondary">
              <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                {(user?.name || "T").slice(0, 1).toUpperCase()}
              </div>
              <div className="text-left leading-tight hidden sm:block">
                <div className="text-xs font-semibold">{user?.name || "Trader"}</div>
                <div className="text-[10px] text-muted-foreground">Pro Trader</div>
              </div>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1 bg-popover" align="end">
            <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
            <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-secondary text-sm text-sell">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </PopoverContent>
        </Popover>

        <div className="flex items-center gap-1.5 h-9 px-2 md:px-3 rounded-full bg-buy/15 border border-buy/30" data-testid="live-status">
          <Circle className={`h-2.5 w-2.5 ${feed.mode === "replay" ? "fill-primary text-primary" : status === "connected" ? "fill-buy text-buy animate-pulse" : "fill-sell text-sell"}`} />
          <span data-testid="topbar-mode" className={`hidden sm:inline text-xs font-semibold ${feed.mode === "replay" ? "text-primary" : "text-buy"}`}>{feed.mode === "replay" ? "REPLAY" : status === "connected" ? "Live Data" : "Connecting"}</span>
          <span className="md:ml-1"><AccountSelector /></span>
        </div>
      </div>
    </header>
  );
}
